import { FileSettings } from "$lib/commands/settings";
import { createDialog, createDialogButton, notify } from "ceyper-common";
import { COMMANDS } from "$lib/commands";
import { file as $file, interaction as $interaction } from "$lib/state";
import { File } from "$lib/wasm/file";
import { derived } from "svelte/store";
import { downloadFileAllFrames, downloadFileFrame, downloadFileGif } from "$lib/image/download";
import ImageBrowser from "$lib/dialog/ImageBrowser.svelte";
import DownloadAsOptions from "$lib/dialog/DownloadAsOptions.svelte";
import type { DownloadAsOptionsValue } from "$lib/dialog/downloadAsOptions";

const fileAvailable = derived($file, (file) => !!file);

export function registerFileCommands() {
    const { add, destroy } = COMMANDS.registerActions("all");
    add(
        "file.save",
        async (auto: boolean = false) => {
            const file = $file.value!;
            await file.save();
        },
        fileAvailable
    );

    add(
        "file.download",
        async () => {
            const file = $file.value!;

            const scale = FileSettings.value["file.export-scale"].value;
            const downloadAll = FileSettings.value["file.export-download-all"].value;

            if (downloadAll && file.getFrames().length > 1) {
                await downloadFileAllFrames(file, {
                    name: file.name,
                    scale,
                });
            } else {
                const frame = $interaction.value.frameIndex.value ?? 0;
                await downloadFileFrame(file, {
                    name: file.name,
                    scale,
                    frame,
                });
            }
        },
        fileAvailable
    );

    add(
        "file.download-as",
        async () => {
            const file = $file.value!;
            const exportOptions: DownloadAsOptionsValue = {
                name: file.name,
                format: FileSettings.value["file.export-format"].value as "png" | "gif",
                scale: FileSettings.value["file.export-scale"].value,
                allFrames: FileSettings.value["file.export-download-all"].value,
                gifLoop: FileSettings.value["file.export-gif-loop"].value,
                gifLoopCount: FileSettings.value["file.export-gif-loop-count"].value,
                gifAlphaMode: FileSettings.value["file.export-gif-alpha-mode"].value as
                    | "transparent-threshold"
                    | "matte-color",
                gifAlphaThreshold: FileSettings.value["file.export-gif-alpha-threshold"].value,
                gifMatteColor: FileSettings.value["file.export-gif-matte-color"].value,
                gifDither: FileSettings.value["file.export-gif-dither"].value,
                gifQuality: FileSettings.value["file.export-gif-quality"].value as
                    | "fast"
                    | "balanced"
                    | "best",
            };

            const result = await createDialog<{ options: DownloadAsOptionsValue }>({
                id: "download-as",
                title: "Download as...",
                fields: {
                    options: {
                        name: "Export options",
                        type: "custom",
                        component: DownloadAsOptions,
                        value: exportOptions,
                    },
                },
                buttons: [createDialogButton.close("Cancel"), createDialogButton.confirm("Save")],
            });
            if (!result) return;

            const options = result.options;
            FileSettings.value["file.export-format"].value = options.format;
            FileSettings.value["file.export-scale"].value = +options.scale;
            FileSettings.value["file.export-download-all"].value = options.allFrames;
            FileSettings.value["file.export-gif-loop"].value = options.gifLoop;
            FileSettings.value["file.export-gif-loop-count"].value = +options.gifLoopCount;
            FileSettings.value["file.export-gif-alpha-mode"].value = options.gifAlphaMode;
            FileSettings.value["file.export-gif-alpha-threshold"].value = +options.gifAlphaThreshold;
            FileSettings.value["file.export-gif-matte-color"].value = options.gifMatteColor;
            FileSettings.value["file.export-gif-dither"].value = options.gifDither;
            FileSettings.value["file.export-gif-quality"].value = options.gifQuality;
            FileSettings.save();

            if (options.format === "gif") {
                try {
                    await downloadFileGif(file, {
                        name: options.name,
                        scale: +options.scale,
                        loop: options.gifLoop,
                        loopCount: +options.gifLoopCount,
                        alphaMode: options.gifAlphaMode,
                        alphaThreshold: +options.gifAlphaThreshold,
                        matteColor: options.gifMatteColor,
                        dither: options.gifDither,
                        quality: options.gifQuality,
                    });
                } catch (error) {
                    notify({
                        type: "message",
                        level: "error",
                        title: "GIF export failed",
                        message: error instanceof Error ? error.message : "Failed to encode GIF.",
                    });
                }
                return;
            }

            if (options.allFrames && file.getFrames().length > 1) {
                await downloadFileAllFrames(file, options);
            } else {
                const frame = $interaction.value.frameIndex.value ?? 0;
                await downloadFileFrame(file, {
                    name: options.name,
                    scale: options.scale,
                    frame,
                });
            }
        },
        fileAvailable
    );

    add("file.new", async () => {
        const result = await createDialog({
            id: "new-file",
            title: "New file",
            fields: {
                name: {
                    name: "Name",
                    type: "string",
                    value: "Image",
                },
                width: {
                    name: "Width",
                    type: "number",
                    value: 32,
                },
                height: {
                    name: "Height",
                    type: "number",
                    value: 32,
                },
            },
            buttons: [createDialogButton.close("Cancel"), createDialogButton.confirm("Create")],
        });
        if (!result) return;

        const file = File.new(+result.width, +result.height, result.name);

        $file.set(file);
    });

    add("file.open", async () => {
        const result = await createDialog({
            id: "open-file",
            title: "Open image",
            fields: {
                name: {
                    name: "Name",
                    type: "string",
                    value: "Image",
                },
                image: {
                    name: "Image",
                    type: "custom",
                    component: ImageBrowser,
                    props: {},
                    value: null! as ImageData,
                },
            },
            buttons: [createDialogButton.close("Cancel"), createDialogButton.confirm("Open")],
        });
        if (!result?.image) return;

        const file = File.fromImageData(result.image, result.name);
        $file.set(file);
    });

    add("file.load", (file) => {
        $file.set(file);
    });

    add(
        "file.rename",
        async (name) => {
            const file = $file.value!;

            if (!name) {
                const result = await createDialog({
                    id: "rename-file",
                    title: "Rename file",
                    fields: {
                        name: {
                            name: "Name",
                            type: "string",
                            value: file.name,
                        },
                    },
                    buttons: [
                        createDialogButton.close("Cancel"),
                        createDialogButton.confirm("Rename"),
                    ],
                });
                name = result?.name;

                if (!name) return;
            }

            file.name = name;
            await file.save();
        },
        fileAvailable
    );

    add(
        "file.resize",
        async (width, height) => {
            const file = $file.value!;

            if (!width || !height) {
                const result = await createDialog({
                    id: "resize-file",
                    title: "Resize",
                    fields: {
                        width: {
                            name: "Width",
                            type: "number",
                            value: file.width,
                        },
                        height: {
                            name: "Height",
                            type: "number",
                            value: file.height,
                        },
                    },
                    buttons: [
                        createDialogButton.close("Cancel"),
                        createDialogButton.confirm("Resize"),
                    ],
                });

                if (!result) return;
                width = +result.width;
                height = +result.height;
            }

            if (width <= 0 || height <= 0) return;

            file.resize(width, height);
        },
        fileAvailable
    );

    add(
        "file.clear-history",
        async () => {
            const result = await createDialog({
                id: "clear-history",
                title: "Delete history",
                fields: {
                    info: {
                        name: "info",
                        type: "text",
                        value: "Deleting history is a non-reversible action, proceed with caution.",
                    },
                },
                buttons: [createDialogButton.close("Cancel"), createDialogButton.confirm("Delete")],
            });

            if (!result) return;

            const file = $file.value!;
            file.internal.clear_history();
        },
        fileAvailable
    );

    return { destroy };
}
